<template>
  <DraggableContainer>
    <template #content>
      <div class="doodle-toolbox__buttons">
        <div class="doodle-toolbox__brush-control">
          <button
            class="doodle-toolbox__brush-control__button"
            @click="emits('changeDoodleBrushSize', 'decrease')"
          >
            -
          </button>
          <input
            type="color"
            :value="doodleColor"
            class="doodle-toolbox__color-picker"
            @input="selectColor"
          />
          <button
            class="doodle-toolbox__brush-control__button"
            @click="emits('changeDoodleBrushSize', 'increase')"
          >
            +
          </button>
        </div>
        <ButtonText
          :iconLeft="brushIcon"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'free')"
        />
        <ButtonText
          :iconLeft="iconForwardSlashBlue"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'line')"
        />
        <ButtonText
          :iconLeft="iconSquareBlue"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'rectangle')"
        />
        <ButtonText
          :iconLeft="iconCircleBlue"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'circle')"
        />
        <ButtonText
          :iconLeft="iconTypeBlue"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'text')"
        />
        <ButtonText
          :iconLeft="iconEraserBlue"
          size="medium-icon"
          @onClicked="emits('changeDoodleTool', 'eraser')"
        />
        <ButtonPrimary
          :iconLeft="iconDownloadWhite"
          size="medium-icon"
          style="margin-top: 8px"
          @onClicked="downloadDoodle"
        />
        <ButtonPrimary
          :iconLeft="iconX"
          size="medium-icon"
          style="margin-top: 8px"
          @onClicked="emits('closeDoodle')"
        />
      </div>
    </template>
  </DraggableContainer>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import DraggableContainer from '@/components/doodle/DraggableContainer.vue'
import brushIcon from '@/design-system/assets/icons/Icon-Brush-01-Blue.svg'
import iconForwardSlashBlue from '@/design-system/assets/icons/Icon-Forward-Slash-Blue.svg'
import iconSquareBlue from '@/design-system/assets/icons/Icon-Square-Blue.svg'
import iconCircleBlue from '@/design-system/assets/icons/Icon-Circle-Blue.svg'
import iconTypeBlue from '@/design-system/assets/icons/Icon-Type-Blue.svg'
import iconEraserBlue from '@/design-system/assets/icons/Icon-Eraser-Blue.svg'
import iconDownloadWhite from '@/design-system/assets/icons/Icon-Download-White.svg'
import iconX from '@/design-system/assets/icons/Icon-X.svg'
const emits = defineEmits<{
  (e: 'changeDoodleBrushSize', value: string)
  (e: 'changeDoodleColor', value: string)
  (e: 'changeDoodleTool', value: string)
  (e: 'closeDoodle')
}>()
const doodleColor = ref<string>('#a8071a')

async function downloadDoodle() {
  const draggableContainer = document.getElementById('draggable-container')
  if (draggableContainer) draggableContainer.style.display = 'none'
  const doodleFrame = document.getElementById('doodle-frame')
  if (doodleFrame) doodleFrame.style.display = 'none'
  try {
    // inspiration from this source: https://www.codingnepalweb.com/how-to-take-screenshot-vanilla-javascript/
    // asking permission to use a media input to record current tab
    const stream = await navigator.mediaDevices.getDisplayMedia({
      // @ts-expect-error - unclear how this is supposed to work, but has worked in the past, update this soon
      preferCurrentTab: true
    })
    const video = document.createElement('video')
    function videoEventListener() {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.play() // playing the video so the drawn image won't be black or blank
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height) // drawing an image of the captured video stream
      stream.getVideoTracks()[0].stop() // terminating first video track of the stream
      const image = canvas.toDataURL('image/png')
      downloadImage(image, 'DB doodle.png')
    }
    video.addEventListener('loadedmetadata', videoEventListener)
    video.srcObject = stream // passing capture stream data as video source object
    await new Promise((resolve) => setTimeout(resolve, 100)) // The delay is for making sure the image is saved before the container is displayed again and for the event listener to not be removed too soon
    video.removeEventListener('loadedmetadata', videoEventListener)
  } catch (err) {
    // alert("Failed to capture screenshot!") // if image couldn't capture by any reason, then alert the msg
    console.error('Error: ' + err)
  }
  if (draggableContainer) draggableContainer.style.display = 'flex'
  if (doodleFrame) doodleFrame.style.display = 'unset'
}
function downloadImage(image, filename) {
  var lnk = document.createElement('a'),
    e
  lnk.download = filename
  lnk.href = image
  if (document.createEvent) {
    e = document.createEvent('MouseEvents')
    e.initMouseEvent(
      'click',
      true,
      true,
      window,
      0,
      0,
      0,
      0,
      0,
      false,
      false,
      false,
      false,
      0,
      null
    )
    lnk.dispatchEvent(e)
  }
}
function selectColor(event) {
  doodleColor.value = event.target.value
  emits('changeDoodleColor', event.target.value)
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.doodle-toolbox {
  &__buttons {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
    width: 104px;
  }
  &__color-picker {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: transparent;
    border: none;
    cursor: pointer;
    height: 44px;
    margin-left: 4px;
    padding: 0;
    width: 40px;
    &::-webkit-color-swatch {
      border: none;
      border-radius: 50%;
    }
    &::-moz-color-swatch {
      border: none;
      border-radius: 50%;
    }
  }
  &__brush-control {
    align-items: center;
    display: flex;
    gap: 8px;
    height: 20px;
    justify-content: space-evenly;
    margin-bottom: 8px;
    margin-top: 8px;
    &__button {
      all: unset;
      color: variables.$color-primary;
      cursor: pointer;
      @include font-mixins.desktop-bold-h4;
      height: 16px;
      line-height: 16px;
      &:focus-visible {
        outline: auto;
      }
    }
  }
}
</style>
